import * as React from "react";
import { Table } from "../../../newcomponents/table/table";
import { useTableKindId } from "../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../newcomponents/table/data/useTableItemQueryData";
import { TableHeaderRefreshButton } from "../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionNoLinkColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { linksOf } from "../../../services/cpln";
import { Mk8sCluster } from "../../../schema/types/mk8s";
import { Mk8sClusterTableItem as Mk8sClusterTableItemBase, mk8sHelpers } from "../../../mst/kinds/mk8s.helpers";
import { Base } from "../../../schema/types/base";
import { Modal } from "antd";
import { NGButton } from "../../../newcomponents/button/Button";

interface Mk8sClusterTableItem extends Mk8sClusterTableItemBase {
  selfLink: string;
}

interface TargetTableItem extends Base {
  selfLink: string;
}

interface Props {
  title: string;
  selectionsToRemove: string[];
  onSelectionsToRemoveChange: (values: string[], items?: TargetTableItem[]) => void;
  selectionsToAdd: string[];
  onSelectionsToAddChange: (values: string[], items?: TargetTableItem[]) => void;
  targets: TargetTableItem[];
  onRemove: () => void;
  onOkAddModal: () => void;
}
export const KubernetesTable: React.FC<Props> = ({
  title, //
  selectionsToRemove,
  onSelectionsToRemoveChange,
  selectionsToAdd,
  onSelectionsToAddChange,
  targets,
  onRemove,
  onOkAddModal,
}) => {
  const [isAddingItem, setIsAddingItem] = React.useState(false);

  const { kind, id } = useTableKindId("mk8s", "policy-detail-target-list-mk8s");
  const qData = useTableItemQueryData<Mk8sCluster>(kind, { fetchAllPages: true });

  const targetLinks = targets.map((t) => t.selfLink);
  const mk8sClusterTableItems: Mk8sClusterTableItem[] = qData.visibleItems //
    .map((item) => {
      const provider = mk8sHelpers.getProvider(item);
      const kubernetesVersion = mk8sHelpers.getKubernetesVersion(item);
      const dashboardUrl = mk8sHelpers.getDashboardUrl(item);
      const headlampUrl = mk8sHelpers.getHeadlampUrl(item);
      const isAddonLogsEnabled = mk8sHelpers.getIsAddonLogsEnabled(item);
      const selfLink = linksOf(item).self!;

      return {
        ...item,
        provider: provider,
        kubernetesVersion: kubernetesVersion,
        dashboardUrls: { dashboard: dashboardUrl, headlamp: headlampUrl },
        isAddonLogsEnabled: isAddonLogsEnabled,
        selfLink: selfLink,
      };
    })
    .filter((identity) => {
      return !targetLinks.includes(identity.selfLink);
    });

  return (
    <>
      <Table<TargetTableItem>
        title={title}
        data={targets}
        tableId={`${id}-targets`}
        selectMode="multi"
        selectKey={"selfLink"}
        selections={selectionsToRemove}
        onSelectionsChange={onSelectionsToRemoveChange}
        headerRenderer={() => {
          return (
            <>
              <NGButton
                variant={"danger"}
                size={"small"}
                outlined
                disabled={selectionsToRemove.length < 1}
                onClick={onRemove}
              >
                Remove
              </NGButton>
              <NGButton variant={"action"} size={"small"} onClick={() => setIsAddingItem(true)}>
                Add
              </NGButton>
            </>
          );
        }}
        columns={[
          NameDescriptionNoLinkColumn(), //
          TagsColumn(),
          CreatedColumn(),
          LastModifiedColumn(),
        ]}
      />
      {isAddingItem ? (
        <Modal
          width={"90vw"}
          styles={{ body: { height: "70vh", padding: 0 } }}
          open={isAddingItem}
          onCancel={() => setIsAddingItem(false)}
          footer={
            <div className="modal-actions">
              <NGButton variant="secondary" onClick={() => setIsAddingItem(false)}>
                Cancel
              </NGButton>
              <NGButton
                variant="primary"
                onClick={() => {
                  onOkAddModal();
                  setIsAddingItem(false);
                }}
              >
                OK
              </NGButton>
            </div>
          }
          destroyOnClose={true}
          closable={false}
          maskClosable={false}
        >
          <div className="h-full overflow-auto p-1">
            <Table<Mk8sClusterTableItem>
              title={title}
              data={mk8sClusterTableItems}
              tableId={id}
              selectMode="multi"
              selectKey={"selfLink"}
              selections={selectionsToAdd}
              onSelectionsChange={onSelectionsToAddChange}
              headerRenderer={() => {
                return (
                  <>
                    <TableHeaderRefreshButton onClick={qData.fetchItems} />
                    <CustomTableCPLNQuery
                      tableId={id}
                      kind={kind}
                      onQuery={qData.setQuery}
                      isLoading={qData.isLoadingQueried}
                    />
                  </>
                );
              }}
              enableFilter
              isLoading={qData.isLoading}
              columns={[
                NameDescriptionNoLinkColumn(),
                {
                  id: "provider",
                  label: "Provider",
                  canGroupByDistinctValues: true,
                  groupLabelMap: {
                    generic: "Generic",
                    hetzner: "Hetzner",
                    aws: "AWS",
                    ephemeral: "Ephemeral",
                    linode: "Linode",
                    oblivus: "Oblivus",
                    lambdalabs: "Lambdalabs",
                    paperspace: "Paperspace",
                  },
                  size: 160,
                },
                {
                  id: "kubernetesVersion",
                  label: "Version",
                  canGroupByDistinctValues: true,
                  enableHide: true,
                  size: 160,
                },
                TagsColumn(),
                CreatedColumn(),
                LastModifiedColumn(),
              ]}
            />
          </div>
        </Modal>
      ) : null}
    </>
  );
};
